import dayjs from "dayjs";
import { JobAction } from "~/interface/Job";
import { normalizeTableAlias } from "~/utils/tableAlias";
import { buildAddHistoryItems, buildVoidHistoryItems } from "~/class/AdapterMobileHistoryV1";

// History mirror v2 — converts a SPA jobchain card into a per-table history
// record on the ALIAS-keyed node
//   customer/{storeId}/mobile_v2_history/{alias}/{sanitizedJobId}
//
// Why a second adapter instead of widening V1: the legacy node is keyed by the
// fragile 1-based numeric table id (renumbers whenever a zone is added/renamed),
// and it deliberately skips phone-originated ADDs because a mobile-v1 phone wrote
// its own history record as part of its transport. mobile-v2 phones write no
// history at all, so those orders are missing from the legacy node entirely —
// including them here is the whole point of this silo.
//
// Differences from V1:
//   - keyed by the NORMALIZED alias (the jobchain's own key space), not a number
//   - the child key is the sanitized jobId, NOT a push key, so writing the same
//     card twice overwrites one node instead of adding a second row
//   - source === 'm' ADDs are INCLUDED (mobile-v2 orders)
//   - the child VALUE is a raw object, not a JSON string
//   - `ts` (epoch millis) added — what the Android mapper reads; `datetime` is
//     kept in the legacy RFC822 form for compatibility
//
// `read: 2` MUST stay in the record: watchFromMobileV1 subscribes the legacy
// silo with orderByKey()/startAt(epoch) and push keys sort after integer keys, so
// the cashier can see its own writes; read == 2 is what makes it early-return
// instead of re-ingesting them as fresh mobile orders (pos.mobile-v1.ts:174).
export interface HistoryRecordV2Result {
  alias: string | null;
  /** Deterministic child key — see historyKeyFromJobId. Null when the record is empty. */
  key: string | null;
  record: any | null;
}

// RTDB forbids . # $ [ ] / in keys. The jobchain sanitizes its own key with
// `job.jobId.replaceAll(".", "_")` (pos.jobs.ts:113) — the ISO timestamp is the only
// realistic source of a dot; the rest are replaced defensively, since a stray "/"
// would silently write a nested path instead of a child.
const RTDB_ILLEGAL_KEY_CHARS = /[.#$\[\]\/]/g;

/**
 * The history child key, derived from `job.jobId` exactly the way the jobchain derives
 * its own — so a retried / replayed add() lands on the SAME node and overwrites itself
 * instead of pushing a second copy of the row.
 *
 * jobId is `<ISO timestamp>__<alias>` (JobBuilderV1.build), so these keys are also
 * lexicographically chronological, which is what push keys were giving us before.
 */
export function historyKeyFromJobId(jobId: any): string {
  return String(jobId ?? "").replace(RTDB_ILLEGAL_KEY_CHARS, "_");
}

export default class AdapterMobileHistoryV2 {
  fromJobToHistory(job: any): HistoryRecordV2Result {
    const empty: HistoryRecordV2Result = { alias: null, key: null, record: null };
    if (!job) return empty;

    const isVoid = job.type === JobAction.VOID;
    const isAdd = job.type === JobAction.ADD;
    if (!isVoid && !isAdd) return empty;

    const alias = normalizeTableAlias(job.alias, job);
    if (!alias) return empty;

    // No jobId means no idempotent key, and a push() would risk a duplicate row on
    // replay — skip rather than write something we can't overwrite. Every card built
    // through JobBuilderV1 has one.
    const key = historyKeyFromJobId(job.jobId);
    if (!key) return empty;

    // Reinterpreted as an "is this a real table?" predicate: TOGO / JOB /
    // DELIVERY cards carry queue-string aliases and would otherwise land here
    // ($onAction fires for every mode). It also guarantees the alias exists in
    // the table map and is therefore RTDB-key-legal (no . # $ [ ] /).
    const tableNumber = usePosMobileV1Store().convertTableToUuid(alias);
    if (!tableNumber) return empty;

    const orderitems = isVoid ? buildVoidHistoryItems(job) : buildAddHistoryItems(job);
    if (orderitems.length === 0) return empty;

    const employeeName = job.employee?.name || "cashier";

    // pcTimestamp is the cashier's synced clock (what computeJobchain sorts by);
    // job.timestamp on a mobile order is the phone's. Firebase throws on NaN, so
    // an unparsable timestamp must never reach the write.
    const at = dayjs(job.pcTimestamp || job.timestamp);
    const ts = at.isValid() ? at.valueOf() : Date.now();
    const datetime = dayjs(ts).format("YYYY-MM-DD[T]HH:mm:ss.SSSZZ");

    const record = {
      // Always the jobId (guaranteed non-empty by the key check above) — never a
      // random id, which would make a rewrite of the same node differ from the first.
      uniqueid: String(job.jobId),
      username: employeeName,
      employeeName,
      employeeId: job.employee?.employeeId || 0,
      // Best-effort legacy ids, kept for compatibility. `alias` is authoritative.
      table: tableNumber,
      uuid: tableNumber,
      alias,
      jobId: 0,
      customercount: Number(job.guests) || 0,
      datetime,
      ts,
      orderitems,
      comments: job.note || job.__reason || "",
      read: 2,
      isVoid,
      handshake: true,
    };

    return { alias, key, record };
  }
}
